/**
 * Application initialization module
 * Runs pure JS setup before React renders
 */

import { initTelemetry, captureException, flush } from '@/services/telemetry';
import { initWebVitals } from '@/services/webVitals';
import { isFeatureEnabled } from '@/config/featureFlags';
import ApiClient from '@/api/base';
import { useUserStore } from '@/stores/userStore';
import { queryClient } from '@/lib/queryClient';

let initialized = false;

/**
 * Setup global error handlers for uncaught errors and unhandled promise rejections
 */
function setupGlobalErrorHandlers() {
  // Handle uncaught errors
  window.addEventListener('error', event => {
    // Ignore errors from browser extensions
    if (event.filename?.includes('extension')) {
      return;
    }

    captureException(event.error || new Error(event.message), {
      tags: {
        errorType: 'uncaught',
        source: 'window.onerror',
      },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    captureException(error, {
      tags: {
        errorType: 'unhandledRejection',
        source: 'window.onunhandledrejection',
      },
      extra: {
        reason: String(event.reason),
      },
    });
  });

  // Flush telemetry before page unload
  window.addEventListener('beforeunload', () => {
    flush(500);
  });

  // Handle visibility change (user leaving the page)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush(500);
    }
  });
}

/**
 * Initialize the application
 * Call this once before React renders
 */
export function initApp() {
  if (initialized) {
    return;
  }

  // Register token getter so API client auto-injects auth headers
  ApiClient.setTokenGetter(() => useUserStore.getState().user?.token);

  // Clear the query cache whenever the auth token changes (login, logout,
  // account switch) — user and admin sessions share query keys, so stale
  // cross-session data must never survive an identity change.
  let prevToken = useUserStore.getState().user?.token;
  useUserStore.subscribe(state => {
    const token = state.user?.token;
    if (token !== prevToken) {
      prevToken = token;
      queryClient.clear();
    }
  });

  if (!isFeatureEnabled('enableTelemetry')) {
    initialized = true;
    return;
  }

  // Initialize telemetry service (Sentry)
  initTelemetry();

  // Initialize Web Vitals monitoring
  initWebVitals();

  // Setup global error handlers
  setupGlobalErrorHandlers();

  initialized = true;

  if (import.meta.env.MODE === 'development') {
    console.info('[init] Application initialized');
  }
}

export default initApp;
